import React, { useMemo } from "react";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from 'react-i18next';

/**
 * Student Hub Premium Hero Component
 * Redesigned: NO search bar (search lives in category pages)
 * 
 * Stack Order: Hero → Tagline → Level Selector → Level Progress → 4-Card Grid
 */

// 2 Core Category Cards — Internships/Jobs & Scholarships disabled (hidden from UI)
const CATEGORY_CARDS = [
  { 
    slug: "books",
    icon: "📚", 
    route: "/student-hub/books",
    title: { en: "Books", ar: "كتب وملخصات" },
  },
  /* DISABLED — Internships & Jobs (code preserved, hidden from UI)
  { 
    slug: "internships",
    icon: "💼", 
    route: "/student-hub/internships",
    title: { en: "Internships & Jobs", ar: "تدريب ووظائف" },
  },
  */
  /* DISABLED — Scholarships (code preserved, hidden from UI)
  { 
    slug: "scholarships",
    icon: "🎓", 
    route: "/student-hub/scholarships",
    title: { en: "Scholarships", ar: "منح دراسية" },
  },
  */
  { 
    slug: "cv",
    icon: "📝", 
    route: "/student-hub/cv",
    title: { en: "CV & Cover Letter", ar: "السيرة الذاتية" },
  },
];

// Academic levels enum (matches backend)
const STUDENT_LEVELS = [
  { key: "secondary", label_en: "Secondary", label_ar: "ثانوي" },
  { key: "university", label_en: "University", label_ar: "جامعي" },
  { key: "postgrad", label_en: "Postgrad", label_ar: "دراسات عليا" },
  { key: "vocational", label_en: "Vocational", label_ar: "مهني/تقني" },
  { key: "other", label_en: "Other", label_ar: "أخرى" },
];

/**
 * SYNCHRONOUS RTL detection
 */
function getIsRTL(langProp) {
  if (typeof document !== 'undefined') {
    if (document.dir === "rtl" || document.documentElement.dir === "rtl") {
      return true;
    }
  }
  if (langProp === "ar" || langProp?.startsWith("ar")) {
    return true;
  }
  try {
    const storedLang = localStorage.getItem('i18nextLng') || 
      localStorage.getItem('lang') || 
      localStorage.getItem('language');
    if (storedLang?.startsWith('ar')) return true;
  } catch { }
  return false;
}

export default function StudentHubHero({
  lang = "ar",
  onBack,
  onCategoryClick,
  // Level selector props
  selectedLevel,
  onLevelChange,
}) {
  const isRTL = getIsRTL(lang);
  const { t } = useTranslation();

  return (
    <div className="student-hero-container">
      {/* Compact gradient header */}
      <div className="student-hero-bg">
        <div className="student-hero-geometric" />
        
        {/* Back arrow — 40×40, top-left aligned with page padding */}
        <button 
          className="student-hero-nav-btn"
          onClick={onBack}
          data-testid="hero-back-btn"
        >
          <ArrowLeft size={20} className={isRTL ? 'rtl-flip' : ''} />
        </button>

        {/* Content stack: badge → title → subtitle */}
        <div className="student-hero-content">
          <span className="student-hero-badge">
            {t('shh.learnBuildGrow')}
          </span>
          <h1 className="student-hero-title" data-testid="hero-title">
            {t('shh.studentHub')}
          </h1>
          <p className="student-hero-subtitle">
            {t('shh.booksScholarshipsOpportunitiesCvBuilder')}
          </p>
        </div>
      </div>

      {/* Level Selector */}
      <div className="student-level-selector" data-testid="level-selector">
        <span className="level-selector-label" data-testid="level-selector-label">
          {t('shf.yourAcademicLevel')}
        </span>
        <div className="level-selector-scroll">
          {STUDENT_LEVELS.map((level) => (
            <button
              key={level.key}
              className={`level-chip ${selectedLevel === level.key ? 'active' : ''}`}
              onClick={() => onLevelChange?.(level.key)}
              data-testid={`level-chip-${level.key}`}
            >
              {isRTL ? level.label_ar : level.label_en}
            </button>
          ))}
        </div>
      </div>

      {/* 4-Card Core Grid (2x2) */}
      <div className="student-category-grid-4" data-testid="student-category-grid">
        {CATEGORY_CARDS.map((cat) => (
          <button
            key={cat.slug}
            className="student-category-card-premium"
            onClick={() => onCategoryClick?.(cat.slug, cat.route)}
            data-testid={`category-${cat.slug}`}
          >
            <div className="card-icon">{cat.icon}</div>
            <div className="card-title">{isRTL ? cat.title.ar : cat.title.en}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// Export level constants for use elsewhere
export { STUDENT_LEVELS, CATEGORY_CARDS };
